import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import Navbar from '../Navbar';
import Footer from '../Footer';
import { useAuth } from '../../context/AuthContext';
import { useAuthGate } from '../../context/AuthGateContext';
import {
  MapPin, Heart, Share2, CheckCircle, Building2,
  Loader, Wifi, Dumbbell, Car, Search, ArrowLeft
} from 'lucide-react';
import { Badge } from '../ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;
const PLACEHOLDER = 'https://images.pexels.com/photos/1545743/pexels-photo-1545743.jpeg?auto=compress&cs=tinysrgb&w=600';

const TYPE_LABELS = {
  city: 'City / Suburb',
  university: 'University',
  property: 'Property Name',
};

export default function SearchResults() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { requireAuth } = useAuthGate();

  const q = searchParams.get('q') || '';
  const type = searchParams.get('type') || 'city';

  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('rentalx_fav') || '[]')); }
    catch { return new Set(); }
  });
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    if (!q) { setLoading(false); return; }
    setLoading(true);
    fetch(`${API}/api/properties?q=${encodeURIComponent(q)}&search_type=${type}&limit=50`)
      .then(r => r.ok ? r.json() : [])
      .then(data => setResults(Array.isArray(data) ? data : []))
      .catch(() => setResults([]))
      .finally(() => setLoading(false));
  }, [q, type]);

  const handleCardClick = (id) => {
    if (!user) { requireAuth(`/property/${id}`); return; }
    navigate(`/property/${id}`);
  };

  const toggleFav = (id, e) => {
    e.stopPropagation();
    setFavorites(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      localStorage.setItem('rentalx_fav', JSON.stringify([...next]));
      return next;
    });
  };

  const share = (id, e) => {
    e.stopPropagation();
    const url = `${window.location.origin}/property/${id}`;
    navigator.clipboard?.writeText(url).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    }).catch(() => {});
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="pt-16">
        {/* Header */}
        <div className="bg-gray-50 border-b border-gray-100 py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link to="/" className="flex items-center text-[#094f6f] text-sm font-medium hover:opacity-70 mb-4">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back to Search
            </Link>
            <div className="flex items-center gap-3">
              <Search className="w-6 h-6 text-[#094f6f]" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {loading ? 'Searching...' : `${results.length} result${results.length !== 1 ? 's' : ''} found`}
                </h1>
                <p className="text-gray-500 text-sm">
                  Searching <span className="font-medium text-[#094f6f]">{TYPE_LABELS[type] || type}</span> for{' '}
                  <span className="font-semibold text-gray-800">"{q}"</span>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-24">
              <Loader className="w-10 h-10 text-[#094f6f] animate-spin mb-4" />
              <p className="text-gray-500">Searching properties near <strong>{q}</strong>…</p>
            </div>
          ) : results.length === 0 ? (
            <div className="text-center py-24">
              <Building2 className="w-16 h-16 text-gray-200 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-700 mb-2">No properties found</h2>
              <p className="text-gray-400 text-sm mb-6">
                We couldn't find any listings matching <strong>"{q}"</strong> for {TYPE_LABELS[type]}.
              </p>
              <div className="space-y-2 text-sm text-gray-500">
                <p>Try:</p>
                <ul className="list-disc list-inside text-left inline-block">
                  <li>Checking the spelling of the city or university name</li>
                  <li>Using a broader search term (e.g., "Wollongong" instead of "UOW")</li>
                  <li>Switching the search type (City / University / Property Name)</li>
                </ul>
              </div>
              <Link to="/"
                className="inline-block mt-6 text-white px-6 py-3 rounded-xl font-semibold text-sm"
                style={{ background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' }}>
                Back to Search
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {results.map((prop, i) => (
                <div
                  key={prop.id}
                  className="group cursor-pointer bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100"
                  onClick={() => handleCardClick(prop.id)}
                  data-testid={`search-result-${prop.id}`}
                >
                  {/* Image */}
                  <div className="relative h-52 overflow-hidden">
                    <img
                      src={prop.images?.[0] || PLACEHOLDER}
                      alt={prop.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      onError={e => { e.target.src = PLACEHOLDER; }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

                    {/* Actions */}
                    <div className="absolute top-3 right-3 flex gap-1.5">
                      <button className="w-8 h-8 bg-white/90 rounded-full flex items-center justify-center hover:bg-white"
                        onClick={e => toggleFav(prop.id, e)}>
                        <Heart className={`w-4 h-4 ${favorites.has(prop.id) ? 'fill-red-500 text-red-500' : 'text-gray-600'}`} />
                      </button>
                      <button className="w-8 h-8 bg-white/90 rounded-full flex items-center justify-center hover:bg-white"
                        onClick={e => share(prop.id, e)}>
                        {copiedId === prop.id
                          ? <CheckCircle className="w-4 h-4 text-green-500" />
                          : <Share2 className="w-4 h-4 text-gray-600" />}
                      </button>
                    </div>

                    {/* Price */}
                    <div className="absolute bottom-3 right-3 bg-white/95 rounded-full px-3 py-1.5">
                      <span className="text-sm font-bold text-gray-900">
                        A${prop.price_per_week}<span className="text-xs font-normal text-gray-500">/wk</span>
                      </span>
                    </div>

                    {/* University tag */}
                    {prop.university_nearby && (
                      <div className="absolute bottom-3 left-3">
                        <span className="bg-[#094f6f]/85 text-white text-xs px-2.5 py-1 rounded-full font-medium">
                          Near {prop.university_nearby}
                        </span>
                      </div>
                    )}

                    {/* Live badge */}
                    <div className="absolute top-3 left-3 bg-green-500 text-white text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wide">
                      Live
                    </div>
                  </div>

                  {/* Info */}
                  <div className="p-4">
                    <h3 className="text-base font-bold text-gray-900 mb-1 group-hover:text-[#094f6f] transition-colors line-clamp-1">
                      {prop.name}
                    </h3>
                    <div className="flex items-center text-gray-500 text-xs mb-3">
                      <MapPin className="w-3 h-3 mr-1 flex-shrink-0" />
                      <span className="line-clamp-1">{prop.city}, Australia</span>
                    </div>

                    <div className="flex flex-wrap gap-1.5 mb-3">
                      <Badge variant="secondary" className="bg-sky-50 text-[#094f6f] text-xs">
                        {prop.room_type}
                      </Badge>
                      {prop.bedrooms > 0 && (
                        <Badge variant="secondary" className="bg-sky-50 text-[#094f6f] text-xs">
                          {prop.bedrooms} bed
                        </Badge>
                      )}
                    </div>

                    {prop.description && (
                      <p className="text-xs text-gray-400 line-clamp-2 mb-3 leading-relaxed">
                        {prop.description}
                      </p>
                    )}

                    <button
                      className="w-full py-2 rounded-xl text-sm font-semibold text-white hover:opacity-90 transition-opacity"
                      style={{ background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' }}
                      onClick={e => { e.stopPropagation(); handleCardClick(prop.id); }}
                    >
                      View Details
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
